import { IPlayerAdapter, PlayerInfo, VideoMetadataType, PlatformType } from '@/services/conviva/conviva.types';
import ConvivaHtml5Module from '@convivainc/conviva-js-html5';
import Conviva, { VideoAnalytics } from '@convivainc/conviva-js-coresdk';
import * as dashjs from 'dashjs';
import { deviceDetector } from '@/services/conviva/device-detector';
import { detectPlatform } from '@/utils/device';
import packageJson from '../../../../package.json';

function getPlatformPlayerName(): string {
  const platform = deviceDetector.deviceMetadata.platform;
  switch (platform) {
    case PlatformType.Tizen:
      return 'Thmanyah_Tizen';
    case PlatformType.WebOS:
      return 'Thmanyah_WebOS';
    case PlatformType.Vidaa:
      return 'Thmanyah_Vidaa';
    case PlatformType.Web:
    default:
      return 'Thmanyah_TV_WEB_EMULATOR';
  }
}

export class DashAdapter implements IPlayerAdapter {
  player: any = null;
  videoElement: HTMLVideoElement | null = null;
  videoAnalytics: VideoAnalytics | null = null;
  private cleanUpCallbacks: Array<() => void> = [];
  private logging = false;
  private isLiveContent: boolean = false;

  private _avgBrWeightedSum = 0;
  private _avgBrTotalMs = 0;
  private _avgBrIntervalKbps = 0;
  private _avgBrIntervalTs = 0;

  private _resetAvgBitrate() {
    this._avgBrWeightedSum = 0;
    this._avgBrTotalMs = 0;
    this._avgBrIntervalKbps = 0;
    this._avgBrIntervalTs = 0;
  }

  private _reportBitrate(kbps: number) {
    if (!this.videoAnalytics || kbps <= 0) return;
    const nowMs = Date.now();
    if (this._avgBrIntervalTs > 0 && this._avgBrIntervalKbps > 0) {
      const elapsed = nowMs - this._avgBrIntervalTs;
      this._avgBrWeightedSum += this._avgBrIntervalKbps * elapsed;
      this._avgBrTotalMs += elapsed;
    }
    this._avgBrIntervalKbps = kbps;
    this._avgBrIntervalTs = nowMs;
    this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.BITRATE, kbps);
    const avgKbps = this._avgBrTotalMs > 0 ? Math.round(this._avgBrWeightedSum / this._avgBrTotalMs) : kbps;
    this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.AVG_BITRATE, avgKbps);
  }

  async createSession({
    assetName = 'NA',
    viewerId,
    isLive = false,
  }: {
    assetName?: string;
    viewerId?: string;
    isLive?: boolean;
  }) {
    await deviceDetector.ready;
    if (process.env.NODE_ENV !== 'production') {
      const platform = typeof window !== 'undefined' ? detectPlatform() : 'web';
      const isTvPlatform = ['webos', 'tizen', 'vidaa'].includes(platform);
      const usingTestGateway = !!(process.env.VUE_APP_CONVIVA_GATEWAY_URL || '').trim();
      if (!isTvPlatform && usingTestGateway) {
        if (this.logging) {
          console.warn('[conviva][dash] Skipping Conviva session: non-TV + test gateway');
        }
        return;
      }
    }

    try {
      const trimmedName = assetName?.trim() || '';
      let validAssetName = trimmedName;
      if (!trimmedName || trimmedName.toUpperCase() === 'NA') {
        console.warn(`[conviva][DashAdapter] Invalid assetName: "${assetName}". Using fallback.`);
        validAssetName = 'Unknown Asset';
      }

      this.isLiveContent = isLive;

      const settings: {
        [Conviva.Constants.GATEWAY_URL]?: string;
        [Conviva.Constants.LOG_LEVEL]?: number;
      } = {};

      if (process.env.VUE_APP_CONVIVA_GATEWAY_URL) {
        settings[Conviva.Constants.GATEWAY_URL] = process.env.VUE_APP_CONVIVA_GATEWAY_URL || '';
      }

      if (this.logging) {
        settings[Conviva.Constants.LOG_LEVEL] = Conviva.Constants.LogLevel.DEBUG;
      }

      Conviva.Analytics.init(process.env.VUE_APP_CONVIVA_CUSTOMER_KEY || '', null, settings);

      Conviva.Analytics.reportDeviceMetric(
        Conviva.Constants.Network.CONNECTION_TYPE,
        deviceDetector.deviceMetadata.connectionType || 'WiFi',
      );

      Conviva.Analytics.setDeviceMetadata({
        [Conviva.Constants.DeviceMetadata.BRAND]: deviceDetector.deviceMetadata.brand,
        [Conviva.Constants.DeviceMetadata.MANUFACTURER]: deviceDetector.deviceMetadata.manufacturer,
        [Conviva.Constants.DeviceMetadata.MODEL]: deviceDetector.deviceMetadata.deviceModel,
        [Conviva.Constants.DeviceMetadata.TYPE]: Conviva.Constants.DeviceType.SMARTTV,
        [Conviva.Constants.DeviceMetadata.OS_NAME]: deviceDetector.deviceMetadata.osName,
        [Conviva.Constants.DeviceMetadata.OS_VERSION]: deviceDetector.deviceMetadata.osVersion,
        [Conviva.Constants.DeviceMetadata.CATEGORY]:
          deviceDetector.deviceMetadata.platform === PlatformType.Web
            ? Conviva.Constants.DeviceCategory.WEB
            : Conviva.Constants.DeviceCategory.SMART_TV,
      });

      this.videoAnalytics = Conviva.Analytics.buildVideoAnalytics();

      // Set framework info before reportPlaybackRequested so it is included in the opening heartbeat
      this.videoAnalytics.setPlayerInfo({
        [Conviva.Constants.FRAMEWORK_NAME]: 'Dash.js',
        [Conviva.Constants.FRAMEWORK_VERSION]: '4.0.0',
      });

      this.videoAnalytics.reportPlaybackRequested({
        [Conviva.Constants.ASSET_NAME]: validAssetName,
        [Conviva.Constants.VIEWER_ID]: viewerId || 'NA',
        [Conviva.Constants.IS_LIVE]: isLive ? 'LIVE' : 'VOD',
        [Conviva.Constants.APPLICATION_VERSION]: packageJson.version,
        [Conviva.Constants.PLAYER_NAME]: getPlatformPlayerName(),
        [Conviva.Constants.FRAMEWORK_NAME]: 'Dash.js',
        [Conviva.Constants.FRAMEWORK_VERSION]: '4.0.0',
      });
    } catch (error) {
      if (this.logging) {
        console.warn('[conviva][dash] Failed to initialize Conviva session:', error);
      }
      this.videoAnalytics = null;
    }
  }

  destroySession() {
    if (!this.videoAnalytics) {
      return;
    }
    this._resetAvgBitrate();
    this.videoAnalytics.release();
    this.videoAnalytics = null;
    this.cleanUpCallbacks.forEach(callback => callback());
    this.cleanUpCallbacks = [];
  }

  setPlayer(playerAndElement: { player: any; videoElement: HTMLVideoElement }) {
    if (!this.videoAnalytics) {
      return;
    }

    this.player = playerAndElement.player;
    this.videoElement = playerAndElement.videoElement;

    setTimeout(() => {
      if (!this.videoAnalytics || !this.videoElement) return;
      this.videoAnalytics.setPlayer(this.videoElement, {
        [Conviva.Constants.CONVIVA_MODULE]: ConvivaHtml5Module,
      });
    }, 0);

    const normalizeErrorMessage = (err: unknown) => {
      if (err instanceof Error) return err.message;
      if (typeof err === 'string') return err;
      try {
        return JSON.stringify(err);
      } catch {
        return String(err);
      }
    };

    const onError = (event: any) => {
      if (!this.videoAnalytics) return;
      const error = event.error;
      if (!error || !error.fatal) return;

      const errors = dashjs.MediaPlayer.errors as any;
      if (
        error.code === errors.MANIFEST_LOADER_ERROR_CODE_PARSE ||
        error.code === errors.MEDIA_PLAYER_ERROR_CODE_MEDIA_KEY_MESSAGE ||
        error.code === errors.MEDIA_PLAYER_ERROR_CODE_BUFFER_STALLED_ERROR
      ) {
        this.videoAnalytics.reportPlaybackError(error?.message || normalizeErrorMessage(error));
      }
    };

    const bitsToKbps = (bps: number) => Math.max(0, Math.round(bps / 1000));

    const onQualityChangeRendered = (event: any) => {
      if (!this.videoAnalytics || !this.player) return;
      try {
        let bitrateInfos: any[] | null = null;
        if (typeof (this.player as any).getBitrateList === 'function') {
          bitrateInfos = (this.player as any).getBitrateList();
        } else if (typeof this.player.getBitrateInfoListFor === 'function') {
          bitrateInfos = this.player.getBitrateInfoListFor('video');
        }
        if (bitrateInfos && bitrateInfos.length > 0) {
          const bitrateInfo = bitrateInfos.find((b: any) => b.qualityIndex === event.newQuality);
          if (bitrateInfo?.bitrate) {
            this._reportBitrate(bitsToKbps(bitrateInfo.bitrate));
          }
        }
      } catch {
        // ignore
      }
    };

    const onStreamInitialized = () => {
      if (!this.videoAnalytics || !this.player || !this.videoElement) return;
      try {
        const streamUrl = this.player.getSource()?.getSource || this.player.getSource() || '';
        this.videoAnalytics.setContentInfo({
          [Conviva.Constants.IS_LIVE]: this.isLiveContent ? 'LIVE' : 'VOD',
          [Conviva.Constants.DURATION]: this.videoElement.duration || 0,
          [Conviva.Constants.STREAM_URL]: typeof streamUrl === 'string' ? streamUrl : '',
        });

        let bitrateInfos: any[] | null = null;
        if (typeof (this.player as any).getBitrateList === 'function') {
          bitrateInfos = (this.player as any).getBitrateList();
        } else if (typeof this.player.getBitrateInfoListFor === 'function') {
          bitrateInfos = this.player.getBitrateInfoListFor('video');
        }
        if (bitrateInfos && bitrateInfos.length > 0) {
          const currentQuality = this.player.getQualityFor('video');
          const bitrateInfo = bitrateInfos.find((b: any) => b.qualityIndex === currentQuality);
          if (bitrateInfo?.bitrate) {
            this._reportBitrate(bitsToKbps(bitrateInfo.bitrate));
          }
        }
      } catch {
        // ignore
      }
    };

    const onPlaybackStarted = () => {
      if (!this.videoAnalytics || !this.player) return;
      try {
        let bitrateInfos: any[] | null = null;
        if (typeof (this.player as any).getBitrateList === 'function') {
          bitrateInfos = (this.player as any).getBitrateList();
        } else if (typeof this.player.getBitrateInfoListFor === 'function') {
          bitrateInfos = this.player.getBitrateInfoListFor('video');
        }
        if (bitrateInfos && bitrateInfos.length > 0) {
          const currentQuality = this.player.getQualityFor('video');
          const bitrateInfo = bitrateInfos.find((b: any) => b.qualityIndex === currentQuality);
          if (bitrateInfo?.bitrate) {
            this._reportBitrate(bitsToKbps(bitrateInfo.bitrate));
          }
        }
      } catch {
        // ignore
      }
    };

    const Events = dashjs.MediaPlayer.events;

    if (typeof (this.player as any).on !== 'function') {
      console.warn('[DashAdapter] player.on() not available — Conviva events will not be tracked');
    } else {
      (this.player as any).on(Events.ERROR, onError);
      (this.player as any).on(Events.QUALITY_CHANGE_RENDERED, onQualityChangeRendered);
      (this.player as any).on(Events.STREAM_INITIALIZED, onStreamInitialized);
      (this.player as any).on(Events.PLAYBACK_STARTED, onPlaybackStarted);

      this.cleanUpCallbacks.push(() => {
        if (this.player && typeof (this.player as any).off === 'function') {
          try {
            (this.player as any).off(Events.ERROR, onError);
            (this.player as any).off(Events.QUALITY_CHANGE_RENDERED, onQualityChangeRendered);
            (this.player as any).off(Events.STREAM_INITIALIZED, onStreamInitialized);
            (this.player as any).off(Events.PLAYBACK_STARTED, onPlaybackStarted);
          } catch {
            // ignore
          }
        }
      });
    }

    // Framerate sampling every 2s via getVideoPlaybackQuality()
    let _lastFrameCount = 0;
    let _lastFrameTime = 0;
    let _frFirstTick = true;
    const frTimer = setInterval(() => {
      if (!this.videoAnalytics || !this.videoElement) return;
      if (typeof this.videoElement.getVideoPlaybackQuality !== 'function') return;
      const q = this.videoElement.getVideoPlaybackQuality();
      const now = performance.now();
      if (_frFirstTick) {
        _frFirstTick = false;
        _lastFrameCount = q.totalVideoFrames;
        _lastFrameTime = now;
        return;
      }
      if (_lastFrameTime > 0 && q.totalVideoFrames > _lastFrameCount) {
        const elapsed = (now - _lastFrameTime) / 1000;
        const fps = Math.round((q.totalVideoFrames - _lastFrameCount) / elapsed);
        if (fps > 0) {
          this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.RENDERED_FRAMERATE, fps);
        }
      }
      _lastFrameCount = q.totalVideoFrames;
      _lastFrameTime = now;
    }, 2000);

    this.cleanUpCallbacks.push(() => clearInterval(frTimer));
  }

  unsetPlayer() {
    this.player = null;
    this.videoElement = null;
  }

  reportAppBackgrounded(): void {
    try {
      Conviva.Analytics.reportAppBackgrounded();
    } catch {
      // ignore
    }
  }

  reportAppForegrounded(): void {
    try {
      Conviva.Analytics.reportAppForegrounded();
    } catch {
      // ignore
    }
  }

  sendCustomEvent(eventName: string, attributes: Record<string, string>): void {
    if (!this.videoAnalytics) return;
    try {
      this.videoAnalytics.reportPlaybackEvent(eventName, attributes);
    } catch (error) {
      if (this.logging) {
        console.warn('[conviva][DashAdapter] Failed to send custom event', eventName, error);
      }
    }
  }

  getPlayerInfo(): PlayerInfo {
    return {
      name: 'Dash.js',
      version: '4.0.0',
    };
  }

  setContentInfo(metadata: VideoMetadataType) {
    if (!this.videoAnalytics) return;
    this.isLiveContent = metadata.isLive || false;

    const trimmedTitle = metadata.title?.trim() || '';
    const validAssetName = trimmedTitle && trimmedTitle.toUpperCase() !== 'NA' ? trimmedTitle : 'Unknown Asset';

    let streamUrl = '';
    try {
      if (this.player) {
        const source = this.player.getSource();
        streamUrl = typeof source === 'string' ? source : '';
      }
    } catch {
      // ignore
    }

    const contentInfo: any = {
      [Conviva.Constants.IS_LIVE]: this.isLiveContent ? 'LIVE' : 'VOD',
      [Conviva.Constants.ASSET_NAME]: validAssetName,
      [Conviva.Constants.VIEWER_ID]: String(metadata.viewerId) || 'NA',
    };
    if (streamUrl) {
      contentInfo[Conviva.Constants.STREAM_URL] = streamUrl;
    }
    if (metadata.custom) {
      Object.assign(contentInfo, metadata.custom);
    }
    this.videoAnalytics.setContentInfo(contentInfo);
  }
}
